/* global process */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import http from 'axios'
import { useNavigate } from 'react-router-dom'
import { Button, Divider, Icon } from '@mui/material'
import Alert from '@mui/material/Alert'

import { withStyles } from '@mui/styles'
import { style } from 'styles'
import { InputText, PayPalBanner } from 'components'
import { formatNumber, saveAccess } from 'core/lib'

import Conditions from './Conditions/Conditions'
import GuestUserData from './GuestUserData/GuestUserData'
import Product from './Products/Product'
import Reservation from './Products/Reservation'

function Order({ classes }) {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const shop = useSelector(state => state.shop)
	const { loading } = useSelector(state => state.ui)
	const userGuest = useSelector(state => state.userGuest)
	const { products, total } = useSelector(state => state.cart)
	const [coupon, setCoupon] = useState('')
	const [couponDiscount, setCouponDiscount] = useState(null)
	const [conditions, setConditions] = useState({
		open: false,
		type: null,
		privacy_flag: false,
		mkt_flag: false,
		adult_flag: false,
	})
	const [delivery, setDelivery] = useState({
		shipping_cost: 0,
		payment_cost: 0,
		total_to_pay: total,
		note: '',
	})
	let compulsoryUserDataBase = ['name', 'surname', 'email', 'mobile']
	// Se devo richiedere la convenzione aggiungo il campo a quelli obbligatori
	if (Number(shop.profiles.ueby_conventions_mode) == 3) {
		compulsoryUserDataBase = [...compulsoryUserDataBase, 'convention']
	}
	const compulsoryUserDataShip = ['ship_address', 'ship_house_number', 'ship_postal_code', 'ship_city']
	const [compulsoryUserData, setCompulsoryUserData] = useState(compulsoryUserDataBase)

	const toggleConditions = (openState, type) => {
		setConditions({
			...conditions,
			open: openState,
			type: openState ? type : null,
		})
	}

	const getEnablesShippingMethods = () => {
		let shippingMethods = shop.shipping_methods
		// Verifico se ho dei prodotti che non sono prenotazioni ed eventualmente rimuovo il metto do di spedizione 8 (servizio non spedibile)
		if (products.filter(product => String(product.key).slice(0, 12) != 'reservation_').length > 0) {
			shippingMethods = shippingMethods.filter(m => Number(m.shipping_method_id) != 8)
		}

		return shippingMethods
	}

	useEffect(() => {
		async function onMount() {
			await saveAccess('checkout')

			// Se ho un solo metodo di spedizione lo imposto
			if (getEnablesShippingMethods().length == 1) {
				handleShippingMethodChange(getEnablesShippingMethods()[0].shipping_method_id)
			}
		}
		onMount()
	}, [])

	useEffect(() => {
		// Quando cambio metodo di spedizione, se ho un solo metodo di pagamento lo imposto
		const shippingMethod = getEnablesShippingMethods().filter(m => Number(m.shipping_method_id) == Number(delivery.shipping_method_id))[0]
		if (shippingMethod != undefined && shippingMethod.payment_methods.length == 1) {
			handlePaymentMethodChange(shippingMethod.payment_methods[0].payment_method_id)
		}
	}, [delivery.shipping_method_id])

	useEffect(() => {
		calculateTotal(delivery)
	}, [total, couponDiscount])

	// Funzione che estrae il prezzo da applicare da più range
	const getPriceToApply = (costs, um) => {
		let lowerValue = null
		let returnPrice = 0
		let compareValue = null

		switch (Number(um)) {
			case 1:
				// Per prezzo
				compareValue = total
				break

			case 2:
				// Per numero di articoli
				compareValue = products.map(product => product.quantity).reduce((a, b) => a + b, 0)
				break

			case 3:
				// Per peso
				compareValue = products.map(product => product.quantity * product.weight).reduce((a, b) => a + b, 0)
				break
		}

		costs.map(c => {
			// Controllo per ogni range che il valore massimo sia maggiore o uguale di compareValue e che sia minore dell'attuale lowerValue
			// Oppure se il range è stringa vuota lo considero come range superiore da usare solo se lowerValue non è ancora settato
			if ((c.split(':')[0] >= compareValue && (!lowerValue || Number(c.split(':')[0]) < Number(lowerValue)))
					|| (c.split(':')[0] == '' && !lowerValue)) {
				lowerValue = c.split(':')[0]
				returnPrice = c.split(':')[1]
			}
		})
		return returnPrice
	}

	// Funzione che aggiunge il prezzo di spedizione o di gestione al totale
	const calculatePrice = (price, priceToAdd) => {
		if (priceToAdd) {
			if (priceToAdd.indexOf('%') != -1) {
				// Ho un costo in percentuale
				return (Number(price) / 100 * Number(priceToAdd.replace(/\D/g,'')))
			} else {
				// Ho un costo in valore assoluto
				return (Number(priceToAdd))
			}
		} else {
			return 0
		}
	}

	const calculateTotal = (deliveryData) => {
		let shippingMethod
		if (deliveryData.shipping_method_id > 0) {
			// Calcolo le spese di spedizione
			shippingMethod = getEnablesShippingMethods().filter(m => Number(m.shipping_method_id) == Number(deliveryData.shipping_method_id))[0]
			if (shippingMethod.costs &&
				(Number(shippingMethod.min_price_free_shipping) == 0 || total < shippingMethod.min_price_free_shipping)) {
				// Ho dei costi di spedizione
				if (shippingMethod.costs.indexOf(':') == -1) {
					// Ho un unico costo di spedizione
					deliveryData.shipping_cost = calculatePrice(total, shippingMethod.costs)
				} else {
					// Ho uno o più range di costo
					const costs = shippingMethod.costs.split(';')
					const priceToApply = getPriceToApply(costs, shippingMethod.costs_um)
					deliveryData.shipping_cost = calculatePrice(total, priceToApply)
				}
			} else {
				// Non ho spese di spedizione
				deliveryData.shipping_cost = calculatePrice(total, '0')
			}
		} else {
			deliveryData.shipping_cost = 0
		}

		if (deliveryData.payment_method_id > 0) {
			// Calcolo le spese di gestione
			const paymentMethod = shippingMethod.payment_methods.filter(m => Number(m.payment_method_id) == Number(deliveryData.payment_method_id))[0]
			const totalWithShipping = total + delivery.shipping_cost
			if (paymentMethod.costs) {
				// Ho dei costi di gestione del pagamento
				if (paymentMethod.costs.indexOf(':') == -1) {
					// Ho un unico costo di spedizione
					deliveryData.payment_cost = calculatePrice(totalWithShipping, paymentMethod.costs)
				} else {
					// Ho uno o più range di costo
					const costs = paymentMethod.costs.split(';')
					const priceToApply = getPriceToApply(costs, paymentMethod.costs_um)
					deliveryData.payment_cost = calculatePrice(totalWithShipping, priceToApply)
				}
			} else {
				// Non ho spese di spedizione
				deliveryData.payment_cost = calculatePrice(totalWithShipping, '0')
			}
		} else {
			deliveryData.payment_cost = 0
		}

		// Aggiungo l'eventuale codice sconto
		let discount = 0
		if (couponDiscount) {
			if (couponDiscount.price > 0) {
				discount = Number(couponDiscount.price)
			} else {
				discount = total / 100 * Number(couponDiscount.percent)
			}
			// Se l'importo dello sconto è maggiore del totale della merce, lo imposto al valore della merce
			if (discount > total) {
				discount = total
			}
		}
		deliveryData.total_to_pay = total - discount + deliveryData.shipping_cost + deliveryData.payment_cost
		setDelivery({ ...deliveryData })
	}

	const handleShippingMethodChange = (shippingMethodId) => {
		const collectDateTimeProfile = Number(shop.profiles.ueby_collect_date_time)
		const needCollectData = [4,5].includes(Number(shippingMethodId)) // Se shippingMethodId è 4 o 5
		let deliveryData = {
			...delivery,
			payment_method_id: null,
			shipping_method_id: shippingMethodId,
		}
		calculateTotal(deliveryData)

		let newCompulsoryUserData = compulsoryUserDataBase
		// Modifico i dati obbligatori se prevede la spedizione
		if (getEnablesShippingMethods().filter(m => Number(m.shipping_method_id) == Number(shippingMethodId))[0].shippable_flag == 1) {
			newCompulsoryUserData = [...newCompulsoryUserData, ...compulsoryUserDataShip]
		}
		if (needCollectData && collectDateTimeProfile == 1) {
			// Ora di ritiro merce obbligatoria
			newCompulsoryUserData = [...newCompulsoryUserData, 'collect_time']
		} else if (needCollectData && collectDateTimeProfile == 2) {
			// Data e ora di ritiro merce obbligatorie
			newCompulsoryUserData = [...newCompulsoryUserData, 'collect_date', 'collect_time']
		}
		if (Number(shippingMethodId) == 6) {
			// Spedizione internazionale, devo specificare la nazione di spedizione
			newCompulsoryUserData = [...newCompulsoryUserData, 'ship_country_id']
		}
		setCompulsoryUserData([...newCompulsoryUserData])
	}

	const handlePaymentMethodChange = (paymentMethodId) => {
		let deliveryData = {
			...delivery,
			payment_method_id: paymentMethodId,
		}
		calculateTotal(deliveryData)
	}

	const handleConditionCheck = (type, checked) => {
		setConditions({
			...conditions,
			[`${type}_flag`]: checked,
		})
	}

	const handleSubmitOrder = async(e) => {
		e.preventDefault()
		let userDataOk = true
		compulsoryUserData.map(field => {
			if (!userGuest[field] || userGuest[field] == '') {
				userDataOk = false
			}
		})
		let billDataOk = true
		if (userGuest.bill_flag) {
			if (!userGuest.company || userGuest.company == '') {
				billDataOk = false
			}
			if ((!userGuest.piva || userGuest.piva == '') && (!userGuest.cf || userGuest.cf == '')) {
				billDataOk = false
			}
			if (!userGuest.bill_address || userGuest.bill_address == '') {
				billDataOk = false
			}
			if (!userGuest.bill_house_number || userGuest.bill_house_number == '') {
				billDataOk = false
			}
			if (!userGuest.bill_postal_code || userGuest.bill_postal_code == '') {
				billDataOk = false
			}
			if (!userGuest.bill_city || userGuest.bill_city == '') {
				billDataOk = false
			}
		}

		if (!conditions.privacy_flag) {
			dispatch({ type: 'setFeedback@ui', message: 'Accettare le condizioni di vendita e trattamento dei dati personali', color: 'error' })
		} else if (Number(shop.profiles.ueby_order_adult_check) == 1 && !conditions.adult_flag) {
			dispatch({ type: 'setFeedback@ui', message: 'Devi dichiarare di essere maggiorenne per poter completare l\'ordine', color: 'error' })
		} else if (!userDataOk) {
			dispatch({ type: 'setFeedback@ui', message: 'Compilare tutti i campi con (*)', color: 'error' })
		} else if (!billDataOk) {
			dispatch({ type: 'setFeedback@ui', message: 'Compilare i dati per la fatturazione', color: 'error' })
		} else {
			products.forEach(product => {
				// Check if it's a reservation
				if (String(product.key).slice(0, 12) == 'reservation_') {
					product.reservation_data = JSON.stringify({
						resource_name: product.resource_name,
						day: product.reservation_day,
						start: product.start,
						end: product.end,
						resource_id: product.resource_id,
						seats: product.seats,
					})
				} else {
					// It's a product
					product.options_desc = product.options.map((option, o) => `${option.name}: ${option.values.map(value => value.value).join(', ')}`).join('\n')
					product.options_val = product.options.map((option, o) => `${option.option_id}:${option.values.map(value => value.option_value_id).join(',')}`).join('\n')
				}
			})

			const sendOrder = await http.post(`${window.__endpoint}/api/order/create`, {
				privacy_flag: conditions.privacy_flag,
				mkt_flag: conditions.mkt_flag,
				adult_flag: conditions.adult_flag,
				total: total,
				...delivery,
				...userGuest,
				products: products,
				discount_code: couponDiscount ? couponDiscount.coupon_code : null,
			})

			// Svuoto il carrello
			if (process.env.NODE_ENV && process.env.NODE_ENV == 'production') {
				dispatch({ type: 'removeAll@cart' })
			}

			// Feedback all'utente
			dispatch({ type: 'setFeedback@ui', message: sendOrder.message, hideDuration: 0 })

			// Vado al pagamento se ricevo il parametro p altrimenti torno alla home
			if (sendOrder.data.p != '') {
				navigate(`/order/payment?p=${sendOrder.data.p}`)
			} else {
				navigate('/')
			}
		}
	}

	const applyCoupon = async() => {
		const check = await http.post(`${window.__endpoint}/api/order/checkCoupon`, {
			coupon: coupon
		})
		if (! check.data) {
			// Codice non valido
			dispatch({ type: 'setFeedback@ui', message: 'Codice sconto non valido', color: 'error' })
		} else {
			// Cancello il codice inserito
			setCoupon('')
			// Aggiungo il codice ai dati dell'ordine e lo applico
			setCouponDiscount(check.data)
		}
	}

	return (<div className={classes.page}>
		<div className={classes.order}>
			<div className="box">
				<h4>
					<Icon className="icon">local_shipping</Icon> Modalità di consegna
				</h4>
				{getEnablesShippingMethods().map((shippingM, sm) => (
					<p key={sm}>
						<label>
							<input
								name="shipping_method_id"
								type="radio"
								value={shippingM.shipping_method_id}
								onChange={(e) => handleShippingMethodChange(e.target.value)}
								checked={Number(delivery.shipping_method_id) == Number(shippingM.shipping_method_id)}
							/>
							{shippingM.shipping_method}
						</label>
						<br/>
						<small>{shippingM.description}</small>
					</p>
				))}
			</div>

			{delivery.shipping_method_id > 0 && <div className="box">
				<h4>
					<Icon className="icon">credit_card</Icon> Modalità di pagamento
				</h4>
				{getEnablesShippingMethods().filter(m => Number(m.shipping_method_id) == Number(delivery.shipping_method_id))[0].payment_methods.map((paymentM, pm) => (
					<p key={pm}>
						<label>
							<input
								name="payment_method_id"
								type="radio"
								value={paymentM.payment_method_id}
								onChange={(e) => handlePaymentMethodChange(e.target.value)}
								checked={Number(delivery.payment_method_id) == Number(paymentM.payment_method_id)}
							/>
							{paymentM.payment_method}
						</label>
						<br/>
						<small>{paymentM.description}</small>
					</p>
				))}
			</div>}

			<div className="box summary">
				<h4>
					<Icon className="icon">list_alt</Icon> Riepilogo ordine
				</h4>

				{/* Prima visualizzo i prodotti */}
				{products.filter(p => String(p.key).slice(0, 12) != 'reservation_').map((product, p) => <Product key={p} product={product} />)}
				{/* Poi visualizzo le prenotazioni */}
				{products.filter(p => String(p.key).slice(0, 12) == 'reservation_').map((product, p) => <Reservation key={p} product={product} />)}

				{/* <p>Totale articoli: <span className="amount">{formatNumber(total)} €</span></p> */}
				{delivery.shipping_cost > 0 && <p>Spese di spedizione: <span className="amount">{formatNumber(delivery.shipping_cost)} €</span></p>}
				{delivery.payment_cost > 0 && <p>Spese di gestione: <span className="amount">{formatNumber(delivery.payment_cost)} €</span></p>}
				<Divider />
				{Number(shop.profiles.ueby_coupon_enable) == 1 && <div style={{ marginTop: 6 }}>
					{!couponDiscount && <React.Fragment>
						<InputText
							label='Codice sconto ...'
							name='surname'
							onChange={(e) => setCoupon(e.target.value)}
							value={coupon}
						/>
						{coupon != '' && <Button color="secondary" variant="contained" type="button" disabled={!!loading} onClick={applyCoupon}>Applica codice sconto</Button>}
					</React.Fragment>}
					{!!couponDiscount && <p style={{ fontSize: '85%' }}>
						Buono sconto: <span className="amount">{couponDiscount.price > 0 ? `${formatNumber(couponDiscount.price)} €` : `${formatNumber(couponDiscount.percent)} %`}</span><br/>
						* lo sconto è applicato ai soli prodotti escluse le spese di gestione e consegna
					</p>}
				</div>}
				<p className="totalAmount">Totale da pagare: <span className="amount">{formatNumber(delivery.total_to_pay)} €</span></p>
				{Number(delivery.shipping_method_id) == 6 && <Alert severity="warning">Attenzione, il costo della spedizione verrà comunicato tramite mail dopo il completamento dell'ordine</Alert>}
			</div>

			{delivery.payment_method_id > 0 && <form onSubmit={handleSubmitOrder}>
				<GuestUserData
					compulsoryData={compulsoryUserData}
					selectedShippingMethodId={Number(delivery.shipping_method_id)}
					isShippable={Number(getEnablesShippingMethods().filter(m => Number(m.shipping_method_id) == Number(delivery.shipping_method_id))[0].shippable_flag)}
				/>

				<div className="box summary">
					<InputText
						label='Note per il venditore'
						name='note'
						onChange={(e) => { setDelivery({
							...delivery,
							note: e.target.value,
						})}}
						value={delivery.note || ''}
						multiline
					/>
				</div>

				<div className="privacy">
					<p><small>
						<label><input type="checkbox" value="1" selected={conditions.privacy_flag} onChange={(e) => handleConditionCheck('privacy', e.target.checked)} /> Dichiaro di aver letto e di accettare le modalità di </label>
						<a onClick={() => { toggleConditions(true, 'privacy_text') }}>trattamento dei dati personali</a> e le <a onClick={() => { toggleConditions(true, 'sell_conditions') }}>condizioni di vendita</a>
					</small></p>
					{Number(shop.profiles.ueby_order_adult_check) == 1 && <p><small>
						<label><input type="checkbox" value="1" selected={conditions.adult_flag} onChange={(e) => handleConditionCheck('adult', e.target.checked)} /> Dichiaro di aver già compiuto 18 anni e - se minore di 18 anni - di essere stato autorizzato dal titolare della responsabilità genitoriale</label>
					</small></p>}
					<p><small>
						<label><input type="checkbox" value="1" selected={conditions.mkt_flag} onChange={(e) => handleConditionCheck('mkt', e.target.checked)} /> Desidero rimanere aggiornato sulle novità del negozio (potrai annullare l'iscirizione in qualsiasi momento)</label>
					</small></p>
					<p className="submit">
						<Button color="primary" variant="contained" type="submit" disabled={!!loading || products.length == 0}>Invia l'ordine</Button>
					</p>
				</div>
			</form>}

			<Conditions
				open={conditions.open}
				type={conditions.type}
				handleClose={() => {  toggleConditions(false) }}
			/>
		</div>
	</div>)
}

export default withStyles(style)(Order)
